import { useEffect } from 'react';
import { FormControl } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AttributesLevels } from './LevelOfCareSection';
import type { ReservationFormInput } from '../schema/reservationForm.schema';
import type { TripArrayFieldContext } from '../machines/createTripArrayFieldMachine';

import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';

import SelectMenu from '@/components/forms/SelectMenu';
import { LevelOfCareContext } from '../machines/levelOfCare.machine';

export function PassengerAssistanceField({
  index,
  availableAttributes,
}: {
  index: number;
  availableAttributes: AttributesLevels;
}) {
  const { t } = useTranslation('common');
  const { control } = useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs.passengerAssistance;
  const levelOfCareActor = fieldRefs.levelOfCare;
  const levelOfCareSnapshot = useFieldActorSnapshot(levelOfCareActor);
  const isGurneySelected =
    (levelOfCareSnapshot.context as LevelOfCareContext).isGurneySelected[
      index
    ] ?? false;

  const watched =
    useWatch({
      control,
      name: `trips.${index}.MobilityAttribute`,
    }) ?? '';

  useEffect(() => {
    const ctx = actor.getSnapshot().context as TripArrayFieldContext;
    if (ctx.values[index] !== watched) {
      actor.send({ type: 'CHANGE', index, value: watched });
    }
  }, [actor, index, watched]);

  return (
    <Controller
      name={`trips.${index}.MobilityAttribute`}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl fullWidth required error={Boolean(fieldState.error)}>
          <SelectMenu
            label={t('reservation.passengerAssistance')}
            name={`trips.${index}.MobilityAttribute`}
            values={availableAttributes.assistance ?? []}
            valueAttr="transportationAttributeUUID"
            labelAttr="name"
            required={true}
            disabled={isGurneySelected}
            control={control}
            rules={{ required: true }}
            onChange={(evt) => {
              const v = String(evt.target.value);
              actor.send({ type: 'CHANGE', index, value: v });
              field.onChange(v);
            }}
          />
        </FormControl>
      )}
    />
  );
}
